import { useEffect } from "react";
import { router } from "expo-router";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  CheckBoxLabelText,
  CheckBoxWithLabel,
  FormTitle,
  InputWithLabel,
} from "@ptsd/uikit";
import { ArrowLeft } from "@tamagui/lucide-icons";
import i18next from "i18next";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Anchor,
  Form,
  Separator,
  styled,
  useMedia,
  View,
  XStack,
} from "tamagui";
import z from "zod";

import { SignupErrorMessage } from "~/component/feature/auth/signup-error";
import { AuthBaseForm } from "~/component/feature/auth/wrappers";

const schema = z
  .object({
    username: z.string().nonempty(i18next.t("signup:validation.required")),

    email: z
      .string()
      .nonempty(i18next.t("signup:validation.required"))
      .regex(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        i18next.t("signup:validation.email.invalidFormat"),
      ),

    phone: z
      .string()
      .regex(
        /^(\+\d{1,2}\s?)?1?-?\.?\s?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/,
        i18next.t("signup:validation.phone.invalidFormat"),
      )
      .or(z.literal("")),

    password: z
      .string()
      .nonempty(i18next.t("signup:validation.required"))
      .min(8, i18next.t("signup:validation.password.minLength"))
      .regex(
        /^(?=.*[A-Z])[A-Za-z0-9!@#$%^&*()_+{}[\]:;<>,.?/\\~-]{8,}$/,
        i18next.t("signup:validation.password.minLength"),
      ),

    termsAndConditions: z.boolean().optional(),

    privacyPolicy: z.boolean().optional(),

    "password-confirm": z
      .string()
      .nonempty(i18next.t("signup:validation.required"))
      .min(8, i18next.t("signup:validation.password.minLength")),
  })
  .superRefine(({ password, "password-confirm": confirmPassword }, ctx) => {
    if (password !== confirmPassword) {
      ctx.addIssue({
        path: ["password-confirm"],
        message: i18next.t("signup:validation.password.mismatch"),
        code: z.ZodIssueCode.custom,
      });
    }
  });

export type SignupFormData = z.infer<typeof schema>;

const defaultValues: SignupFormData = {
  username: "",
  email: "",
  phone: "",
  password: "",
  "password-confirm": "",
  termsAndConditions: false,
  privacyPolicy: false,
};

const StyledAnchor = styled(Anchor, {
  target: "_blank",
  color: "inherit",
  cursor: "pointer",
  textDecorationLine: "underline",
});

interface SignupFormProps {
  email?: string | undefined;
  withGoBack?: boolean;
  withPhone?: boolean;
  isError?: boolean;
  errorMessage?: string;
  onSubmit: (data: SignupFormData) => void;
}

export const SignupForm: React.FC<SignupFormProps> = ({
  email,
  isError,
  errorMessage,
  onSubmit,
  withPhone = false,
  withGoBack = false,
}) => {
  const { sm } = useMedia();
  const { t } = useTranslation("signup");

  const { control, setValue, handleSubmit, watch } = useForm<SignupFormData>({
    defaultValues,
    resolver: zodResolver(schema),
  });

  const [termsAndConditions, privacyPolicy] = watch([
    "privacyPolicy",
    "termsAndConditions",
  ]);

  useEffect(() => {
    if (email) setValue("email", email);
  }, [email, setValue]);

  return (
    <AuthBaseForm onSubmit={handleSubmit(onSubmit)}>
      <View width={"100%"} gap={"$5"}>
        <FormTitle>{t("ui.form.title")}</FormTitle>
        <View width={"100%"} gap={"$3"}>
          <View width={"100%"} gap={"$4"}>
            <Controller
              control={control}
              name={"username"}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <InputWithLabel
                  data-id={"input-username"}
                  variant="inline"
                  size="md"
                  isError={!!fieldState.error?.message}
                  error={fieldState.error?.message}
                  label={t("ui.form.username.label")}
                  placeholder={t("ui.form.username.placeholder")}
                  onChangeText={(text) => field.onChange(text)}
                  {...field}
                />
              )}
            />

            <Controller
              control={control}
              name={"email"}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <InputWithLabel
                  data-id={"input-email"}
                  readOnly={!!email}
                  variant="inline"
                  size="md"
                  isError={!!fieldState.error?.message}
                  error={fieldState.error?.message}
                  label={t("ui.form.email.label")}
                  placeholder={t("ui.form.email.placeholder")}
                  onChangeText={(text) => field.onChange(text)}
                  {...field}
                />
              )}
            />

            {withPhone && (
              <Controller
                control={control}
                name={"phone"}
                render={({ field, fieldState }) => (
                  <InputWithLabel
                    variant="inline"
                    size="md"
                    isError={!!fieldState.error?.message}
                    error={fieldState.error?.message}
                    label={t("ui.form.phone.label")}
                    placeholder={t("ui.form.phone.placeholder")}
                    onChangeText={(text) => field.onChange(text)}
                    {...field}
                  />
                )}
              />
            )}

            <Controller
              control={control}
              name={"password"}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <InputWithLabel
                  data-id={"input-password"}
                  secureTextEntry
                  variant="inline"
                  size="md"
                  isError={!!fieldState.error?.message}
                  error={fieldState.error?.message}
                  isMessage
                  label={t("ui.form.password.label")}
                  message={t("ui.form.password.message")}
                  placeholder={t("ui.form.password.placeholder")}
                  onChangeText={(text) => field.onChange(text)}
                  {...field}
                />
              )}
            />

            <Controller
              control={control}
              name={"password-confirm"}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <InputWithLabel
                  data-id={"input-password-confirm"}
                  secureTextEntry
                  variant="inline"
                  size="md"
                  isMessage
                  message={t("ui.form.password.message")}
                  isError={!!fieldState.error?.message}
                  error={fieldState.error?.message}
                  label={t("ui.form.passwordConfirm.label")}
                  placeholder={t("ui.form.passwordConfirm.placeholder")}
                  onChangeText={(text) => field.onChange(text)}
                  {...field}
                />
              )}
            />
          </View>

          <View gap={"$2"}>
            <Separator borderColor={"$gray-200"} width={"100%"} />
            <View gap={"$2"}>
              <Controller
                control={control}
                name={"termsAndConditions"}
                render={({ field }) => (
                  <CheckBoxWithLabel
                    data-id={"checkbox-terms"}
                    onCheckedChange={(checked) => {
                      if (checked !== "indeterminate") field.onChange(checked);
                    }}
                    renderLabel={
                      <CheckBoxLabelText>
                        {t("ui.form.termsAndConditions.agree")}
                        <StyledAnchor href={"https://healthymind.help/terms"}>
                          {t("ui.form.termsAndConditions.desc")}
                        </StyledAnchor>
                      </CheckBoxLabelText>
                    }
                  />
                )}
              />

              <Controller
                control={control}
                name={"privacyPolicy"}
                render={({ field }) => (
                  <CheckBoxWithLabel
                    data-id={"checkbox-privacy"}
                    onCheckedChange={(checked) => {
                      if (checked !== "indeterminate") field.onChange(checked);
                    }}
                    renderLabel={
                      <CheckBoxLabelText>
                        {t("ui.form.privacyPolicy.agree")}
                        <StyledAnchor href={"https://healthymind.help/privacy"}>
                          {t("ui.form.privacyPolicy.desc")}
                        </StyledAnchor>
                      </CheckBoxLabelText>
                    }
                  />
                )}
              />
            </View>

            <Separator borderColor={"$gray-200"} width={"100%"} />
          </View>

          {isError && <SignupErrorMessage message={errorMessage} />}
        </View>
      </View>
      <XStack gap={"$1"}>
        {withGoBack && (
          <Button
            data-id={"button-back"}
            mode="icon"
            variant="gray"
            size={sm ? "md" : "lg"}
            rounded="lg"
            onPress={router.back}
          >
            <ArrowLeft size={"$2.5"} color="$gray-primary-900" />
          </Button>
        )}

        <Form.Trigger asChild>
          <Button
            data-id={"button-submit"}
            flex={1}
            width="100%"
            variant="primary"
            size={sm ? "md" : "lg"}
            rounded="lg"
            disabled={!(termsAndConditions && privacyPolicy)}
          >
            {t("ui.form.actions.submit.label")}
          </Button>
        </Form.Trigger>
      </XStack>
    </AuthBaseForm>
  );
};
